import { OrbVisualizer } from '@qi/ui';
import { createFileRoute } from '@tanstack/react-router';

export const Route = createFileRoute('/_marketing/privacy')({
  component: PrivacyPage,
});

function PrivacyPage() {
  return (
    <div className="mx-auto max-w-3xl px-6 py-16">
      <div className="mb-10 flex flex-col items-center gap-4 text-center">
        <OrbVisualizer size={64} rings={2} />
        <p className="font-mono text-xs uppercase tracking-[0.32em] text-ink-2">Privacy</p>
        <h1 className="font-display text-5xl text-ink-0">What we measure. What we keep.</h1>
        <p className="max-w-xl text-ink-1">Plain language. Updated when behavior changes.</p>
      </div>

      <article className="space-y-10 text-ink-1">
        <Section title="Local-first by default">
          <p>
            Webcam frames and microphone audio are processed in your browser. We do not upload raw
            frames or audio. Aggregated wellness scores — never raw biometric data — sync to your
            account only after you sign in.
          </p>
        </Section>

        {/* Storage symmetry pair — matching glass cards so "what we store" and
            "what we never store" read as a single, balanced commitment. */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Section title="What we store" glass>
            <ul className="ml-5 list-disc space-y-1">
              <li>Account email (after you sign up)</li>
              <li>Wellness score summaries: resonance, stress load, focus, recovery, energy</li>
              <li>Frequency protocols you have run</li>
              <li>Session ratings if you give them</li>
            </ul>
          </Section>
          <Section title="What we never store" glass>
            <ul className="ml-5 list-disc space-y-1">
              <li>Raw webcam frames</li>
              <li>Raw microphone audio</li>
              <li>Identifiable biometric features outside aggregate scores</li>
            </ul>
          </Section>
        </div>

        <Section title="Analytics">
          <p>
            On by default for signed-in accounts. We collect anonymized product usage events via
            PostHog (US) to improve the app — you can opt out anytime in Settings. To keep the app
            stable, we also collect anonymized crash and error diagnostics from everyone, including
            signed-out visitors; these contain no personal data. Session replay is disabled on every{' '}
            <code className="font-mono text-ink-0">/scan</code> and{' '}
            <code className="font-mono text-ink-0">/session</code> route, always.
          </p>
        </Section>
        <Section title="Your rights">
          <p>
            Delete your data at any time from Settings — a confirmation email lands within 7 days.
            We do not sell biometric data. We never will.
          </p>
        </Section>
        <Section title="Not a medical device">
          <p>
            Qi Scan supports wellness optimization. It does not diagnose, cure, or replace medical
            professionals. If something feels wrong, see a clinician.
          </p>
        </Section>
        <p className="font-mono text-xs uppercase tracking-[0.18em] text-ink-3">
          Last updated · 2026-06-01
        </p>
      </article>
    </div>
  );
}

function Section({
  title,
  glass = false,
  children,
}: {
  title: string;
  glass?: boolean;
  children: React.ReactNode;
}) {
  return (
    <section className={glass ? 'qi-glass space-y-2 rounded-lg p-6' : 'space-y-2'}>
      <h2 className="font-display text-2xl text-ink-0">{title}</h2>
      <div className="text-sm">{children}</div>
    </section>
  );
}
